import { Box, Button, Typography, CircularProgress, Grid } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import getTheme from "../../theme/theme";
import { PinnacleThemes } from "../../theme/types";
import { register } from "../../api/services/auth";
import { PREBOARD_LOGO_WITH_TEXT_URL, PREBOARD_AUTH_BANNER_DESKTOP_URL, PREBOARD_AUTH_BANNER_MOBILE_URL } from "../../utils/constants";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { handleApiError } from "../../utils/errorHandler";

export default function RegistrationPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();

    const [formDisabled, setFormDisabled] = useState<boolean>(false);

    // First Name
    const [firstName, setFirstName] = useState('');
    const [firstNameTouched, setFirstNameTouched] = useState(false);
    const isFirstNameValid = (firstName ?? '').trim().length > 0;


    // Last Name
    const [lastName, setLastName] = useState('');
    const [lastNameTouched, setLastNameTouched] = useState(false);
    const isLastNameValid = lastName.trim().length > 0;

    // Email
    const [email, setEmail] = useState<string>('');
    const [emailTouched, setEmailTouched] = useState(false);
    const isEmailValid = email.trim().length > 0;

    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(e.target.value);
        if (!firstNameTouched) setFirstNameTouched(true);
    };

    const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(e.target.value);
        if (!lastNameTouched) setLastNameTouched(true);
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (!emailTouched) setEmailTouched(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFirstNameTouched(true);
        setLastNameTouched(true);
        setEmailTouched(true);

        // Early return if invalid, for clarity
        if (!isEmailValid || !isFirstNameValid || !isLastNameValid) {
            return;
        }

        setFormDisabled(true);

        try {
            const registerResponse = await register({
                email,
                firstName,
                lastName
            });
            pushNotification({
                details: registerResponse.message,
                type: NotificationTypes.Success,
            });
            navigate("/login");
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            pushNotification({
                details: errorMessage,
                type: NotificationTypes.Error,
            });
        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <Grid container sx={{ minHeight: '100vh', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left image panel */}
            <Grid
                size={{
                    xs: 12,
                    md: 5
                }}
                sx={{
                    height: { xs: '200px', md: 'auto' },
                    overflow: "hidden",
                    backgroundImage: {
                        xs: `url(${PREBOARD_AUTH_BANNER_MOBILE_URL})`,
                        md: `url(${PREBOARD_AUTH_BANNER_DESKTOP_URL})`
                    },
                    backgroundRepeat: "no-repeat",
                    backgroundSize: { xs: 'cover', md: 'contain' },
                    backgroundPosition: "left top",
                    backgroundColor: "#fff",
                }}
            />

            {/* Right form panel */}
            <Grid
                size={{
                    xs: 12,
                    md: 7
                }}
                sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    alignItems: 'center',
                    placeSelf: 'center',
                    overflowY: 'auto',
                    p: { xs: 2, md: 5 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        alignItems: { xs: 'center', md: 'flex-start' },
                        width: '100%',
                        minHeight: '100%',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: "450px",
                            minWidth: "300px",
                            width: "90%",
                            borderRadius: "5px",
                            boxSizing: "border-box",
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        <img
                            src={PREBOARD_LOGO_WITH_TEXT_URL}
                            alt='pinnacle-logo'
                            style={{
                                width: "300px",
                                height: "auto",
                                alignSelf: "left",
                            }}
                        />
                        <Typography variant="h3" gutterBottom sx={{ mt: 3 }}>
                            Registration
                        </Typography>
                        <form onSubmit={handleSubmit} noValidate>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* FIRST NAME */}
                                <PinnacleTextField
                                    error={firstNameTouched && !isFirstNameValid}
                                    color={firstNameTouched && !isFirstNameValid ? 'error' : 'primary'}
                                    helperText={firstNameTouched && !isFirstNameValid ? 'First name is required' : ''}
                                    id="firstName"
                                    label="First name"
                                    variant="outlined"
                                    autoFocus
                                    onChange={handleFirstNameChange}
                                    onBlur={() => setFirstNameTouched(true)}
                                    value={firstName}
                                    autoComplete="first-name"
                                    required
                                    disabled={formDisabled}
                                />
                                {/* LAST NAME */}
                                <PinnacleTextField
                                    error={lastNameTouched && !isLastNameValid}
                                    color={lastNameTouched && !isLastNameValid ? 'error' : 'primary'}
                                    helperText={lastNameTouched && !isLastNameValid ? 'Last name is required' : ''}
                                    id="lastName"
                                    label="Last name"
                                    variant="outlined"
                                    onChange={handleLastNameChange}
                                    onBlur={() => setLastNameTouched(true)}
                                    value={lastName}
                                    autoComplete="last-name"
                                    required
                                    disabled={formDisabled}
                                />

                                {/* EMAIL */}
                                <PinnacleTextField
                                    error={emailTouched && !isEmailValid}
                                    color={emailTouched && !isEmailValid ? 'error' : 'primary'}
                                    helperText={emailTouched && !isEmailValid ? 'Please enter a valid email' : ''}
                                    id="email"
                                    label="Email Address"
                                    variant="outlined"
                                    onChange={handleEmailChange}
                                    onBlur={() => setEmailTouched(true)}
                                    value={email}
                                    autoComplete="email"
                                    required
                                    disabled={formDisabled}
                                />

                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    type="submit"
                                    disabled={!firstName || !lastName || !email || formDisabled}
                                    sx={{ mb: 1.5 }}
                                >
                                    {formDisabled ? (
                                        <CircularProgress size={24} sx={{ color: blue[300] }} />
                                    ) : (
                                        'CREATE ACCOUNT'
                                    )}
                                </Button>
                                <Button variant="text" fullWidth onClick={() => navigate(-1)}>
                                    <Link
                                        to="/login"
                                        style={{
                                            color: "#373737",
                                            textDecoration: "none",
                                            fontWeight: 500
                                        }}
                                    >
                                        GO BACK
                                    </Link>

                                </Button>
                            </Box>
                        </form>
                    </Box>
                </Box>

            </Grid>
        </Grid>
    );
}